/**
 * S3 Object Key Validation Utility
 * Based on AWS S3 documentation: https://docs.aws.amazon.com/AmazonS3/latest/userguide/object-keys.html
 */

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  suggestedName?: string;
}

export interface FileValidationResult {
  originalName: string;
  validationResult: ValidationResult;
}

/**
 * Characters that are safe to use in S3 object keys
 */
const SAFE_CHARACTERS = /^[a-zA-Z0-9!_.*'()/-]+$/;

/**
 * Patterns that should be avoided
 */
const DISALLOWED_PATTERNS = [
  /^\.+$/, // Only dots
  /\/{2,}/, // Multiple consecutive slashes
  /^\/|\/$/, // Leading or trailing slashes
];

/**
 * Validates a single S3 object key according to AWS rules
 */
export const validateS3ObjectKey = (key: string, existingKeys?: string[]): ValidationResult => {
  const errors: string[] = [];
  const trimmedKey = key.trim();

  // Check if empty after trimming
  if (!trimmedKey) {
    return {
      isValid: false,
      errors: ['File name cannot be empty'],
    };
  }

  // Check length (S3 limit is 1024 bytes in UTF-8) - use trimmed key
  const utf8Length = new TextEncoder().encode(trimmedKey).length;
  if (utf8Length > 1024) {
    errors.push('File name is too long (maximum 1024 bytes in UTF-8)');
  }

  // Check for unsafe characters - use trimmed key
  if (!SAFE_CHARACTERS.test(trimmedKey)) {
    errors.push("File name contains unsupported characters. Only letters, numbers, and !_.*'()-/ are allowed");
  }

  // Check for disallowed patterns - use trimmed key
  for (const pattern of DISALLOWED_PATTERNS) {
    if (pattern.test(trimmedKey)) {
      if (pattern.source === '^.+$') {
        errors.push('File name cannot consist only of dots');
      } else if (pattern.source === '\\/{2,}') {
        errors.push('File name cannot contain consecutive slashes');
      } else if (pattern.source === '^\\/|\\/') {
        errors.push('File name cannot start or end with a slash');
      }
    }
  }

  // Check for null bytes
  if (key.includes('\0')) {
    errors.push('File name cannot contain null bytes');
  }

  // Check for duplicate names - use trimmed key
  if (existingKeys?.includes(trimmedKey)) {
    errors.push('File name already exists. Rename it or check Override to continue.');
  }

  return {
    isValid: errors.length === 0,
    errors,
    suggestedName: errors.length > 0 ? sanitizeS3ObjectKey(key) : undefined,
  };
};

/**
 * Sanitizes an S3 object key by removing/replacing problematic characters
 */
export const sanitizeS3ObjectKey = (key: string): string => {
  let sanitized = key.trim();

  // Replace unsafe characters with underscores
  sanitized = sanitized.replace(/[^a-zA-Z0-9!_.*'()/-]/g, '_');

  // Remove consecutive slashes
  sanitized = sanitized.replace(/\/{2,}/g, '/');

  // Remove leading/trailing slashes
  sanitized = sanitized.replace(/^\/+|\/+$/g, '');

  // Replace sequences of dots with a single dot
  sanitized = sanitized.replace(/\.{2,}/g, '.');

  // Remove leading dots
  sanitized = sanitized.replace(/^\.+/, '');

  // Handle duplicate extensions (e.g., "file.txt.txt" -> "file.txt")
  const extensionMatch = sanitized.match(/(\.[a-zA-Z0-9]+)(\1)+$/);
  if (extensionMatch) {
    sanitized = sanitized.replace(new RegExp(`(${extensionMatch[1].replace('.', '\\.')})\\1+$`), extensionMatch[1]);
  }

  // If empty after sanitization, provide a default
  if (!sanitized) sanitized = 'file';

  return sanitized;
};

/**
 * Validates multiple files and returns validation results
 */
export const validateFiles = (files: File[], existingKeys?: string[]): FileValidationResult[] => {
  return files.map((file) => ({
    originalName: file.name,
    validationResult: validateS3ObjectKey(file.name, existingKeys),
  }));
};

/**
 * Checks if any files have validation errors
 */
export const hasValidationErrors = (results: FileValidationResult[]): boolean => {
  return results.some((result) => !result.validationResult.isValid);
};

/**
 * Gets only files with validation errors
 */
export const getInvalidFiles = (results: FileValidationResult[]): FileValidationResult[] => {
  return results.filter((result) => !result.validationResult.isValid);
};
